import { motion } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ContactForm() {
  const [state, handleSubmit] = useForm("mnnvbzke");
  
  if (state.succeeded) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
      >
        <h3 className="font-semibold text-2xl md:text-3xl mb-2 text-gray-900">Thank You!</h3>
        <p className="text-gray-700 text-sm">Your message has been sent successfully. We'll get back to you soon!</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
    >
      <div className="mb-6">
        <h3 className="font-semibold text-2xl md:text-3xl mb-2 text-gray-900">
          Get In Touch
        </h3>
        <p className="text-gray-600 text-sm">Have a project in mind? Let's make it happen!</p>
      </div>
      
      <form 
        onSubmit={handleSubmit} 
        className="space-y-4 md:space-y-5"
        aria-label="Contact form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="firstName" className="block text-sm font-medium mb-2 text-gray-800">
              First Name <span className="text-red-600">*</span>
            </Label>
            <Input
              id="firstName"
              name="firstName"
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 focus:ring-purple-600 focus:border-purple-600"
              placeholder="John"
              required
            />
            <ValidationError 
              prefix="First Name" 
              field="firstName"
              errors={state.errors}
              className="text-red-600 text-xs mt-1"
            />
          </div>

          <div>
            <Label htmlFor="lastName" className="block text-sm font-medium mb-2 text-gray-800">
              Last Name <span className="text-red-600">*</span>
            </Label>
            <Input
              id="lastName"
              name="lastName"
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 focus:ring-purple-600 focus:border-purple-600"
              placeholder="Doe"
              required
            />
            <ValidationError 
              prefix="Last Name" 
              field="lastName"
              errors={state.errors}
              className="text-red-600 text-xs mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-800">
            Email <span className="text-red-600">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            name="email"
            className="w-full bg-gray-50 border border-gray-300 text-gray-900 focus:ring-purple-600 focus:border-purple-600"
            placeholder="your@email.com"
            required
          />
          <ValidationError 
            prefix="Email" 
            field="email"
            errors={state.errors}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <div>
          <Label htmlFor="subject" className="block text-sm font-medium mb-2 text-gray-800">
            Subject
          </Label>
          <Select 
            name="subject" 
            aria-label="Select subject"
            aria-required="true"
          >
            <SelectTrigger 
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 focus:ring-purple-600 focus:border-purple-600"
              aria-label="Subject selection"
            >
              <SelectValue placeholder="Select a subject" />
            </SelectTrigger>
            <SelectContent 
              className="bg-white border border-gray-300 shadow-lg"
              aria-label="Subject options"
            >
              <SelectItem value="General Inquiry">
                <span aria-label="General Inquiry">General Inquiry</span>
              </SelectItem>
              <SelectItem value="Project Quote">
                <span aria-label="Project Quote">Project Quote</span>
              </SelectItem>
              <SelectItem value="Support">
                <span aria-label="Support">Support</span>
              </SelectItem>
              <SelectItem value="Other">
                <span aria-label="Other">Other</span>
              </SelectItem>
            </SelectContent>
          </Select>
          <input type="hidden" name="_subject" value="New Contact Form Submission" />
        </div>

        <div>
          <Label htmlFor="message" className="block text-sm font-medium mb-2 text-gray-800">
            Message <span className="text-red-600">*</span>
          </Label>
          <Textarea
            id="message"
            name="message"
            rows={4}
            className="w-full bg-gray-50 border border-gray-300 text-gray-900 focus:ring-purple-600 focus:border-purple-600"
            placeholder="Tell us about your project, timeline, and any specific requirements..."
            required
          />
          <ValidationError 
            prefix="Message" 
            field="message"
            errors={state.errors}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <Button 
          type="submit" 
          disabled={state.submitting}
          className="w-full bg-gradient-to-r from-purple-700 to-pink-600 hover:from-purple-800 hover:to-pink-700 text-white shadow-md hover:shadow-lg transition-all"
          aria-label="Submit contact form"
        >
          {state.submitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 transition-transform duration-300 group-hover:translate-x-1">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </>
          )}
        </Button>
        <p className="text-xs md:text-sm text-gray-700 mt-4 text-center leading-tight">
          We typically respond within 24-48 hours. For urgent inquiries, call 
          <a href="tel:+971582481443" className="text-purple-700 hover:underline whitespace-nowrap font-medium">+971-58-248-1443</a>
        </p>
      </form>
    </motion.div>
  );
}
